import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../utils/helpers";
import { styles } from "./adminStyles";

const fmtAge = (sec) => {
  if (sec === null || sec === undefined) return "-";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}dk ${s}sn`;
};

const FiyatGuvenligiTab = ({ profile }) => {
  const [status, setStatus] = useState(null);
  const [threshold, setThreshold] = useState(15);
  const [manualMsg, setManualMsg] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/price-safety/status?profile=${profile || "default"}`);
      setStatus(res.data);
      setThreshold(res.data.threshold_min);
    } catch (e) { console.error(e); }
  }, [profile]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const flash = (ok, msg) => {
    if (ok) { setSuccess(msg); setTimeout(() => setSuccess(""), 3000); }
    else { setError(msg); setTimeout(() => setError(""), 3500); }
  };

  const toggleEnabled = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/price-safety/settings`, { profile, enabled: !status.enabled });
      await load(); flash(true, "Güvenlik modu güncellendi.");
    } catch { flash(false, "Güncellenemedi."); } finally { setBusy(false); }
  };

  const saveThreshold = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/price-safety/settings`, { profile, threshold_min: Number(threshold) });
      await load(); flash(true, "Süre eşiği kaydedildi.");
    } catch { flash(false, "Kaydedilemedi."); } finally { setBusy(false); }
  };

  const doFreeze = async () => {
    if (!window.confirm("Fiyatları manuel olarak dondurmak istediğinize emin misiniz? Müşteri sayfasında tüm fiyatlar '-' görünecek.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/price-safety/freeze`, { profile, message: manualMsg.trim() || undefined });
      setManualMsg(""); await load(); flash(true, "Fiyatlar donduruldu, bildirim gönderildi.");
    } catch { flash(false, "Dondurulamadı."); } finally { setBusy(false); }
  };

  const doUnfreeze = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/price-safety/unfreeze`, { profile });
      await load();
      flash(true, res.data.frozen ? "Manuel kaldırıldı ama doğrulama hâlâ eski — otomatik donma sürüyor." : "Fiyatlar tekrar açıldı, bildirim gönderildi.");
    } catch { flash(false, "Açılamadı."); } finally { setBusy(false); }
  };

  if (!status) {
    return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Yükleniyor...</div>;
  }

  const frozen = status.frozen;
  const reasonLabel = status.frozen_reason === "manual" ? "Manuel" : status.frozen_reason === "auto" ? "Otomatik (doğrulama eski)" : "-";

  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "30px 20px" }}>
      <div style={styles.panel} data-testid="fiyat-guvenligi-panel">
        <h1 style={styles.title}>🛡️ Fiyat Güvenliği</h1>
        {success && <p style={styles.success} data-testid="fg-success">{success}</p>}
        {error && <p style={styles.error} data-testid="fg-error">{error}</p>}

        {/* Durum karti */}
        <div data-testid="fg-status-card" style={{
          ...styles.section, marginBottom: 18, border: `2px solid ${frozen ? "#ef4444" : "#22c55e"}`,
          background: frozen ? "#fef2f2" : "#f0fdf4",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ width: 12, height: 12, borderRadius: 999, background: frozen ? "#ef4444" : "#22c55e" }} />
            <h3 style={{ margin: 0, color: frozen ? "#991b1b" : "#166534", fontSize: 17 }}>
              {frozen ? "FİYATLAR DONDURULDU" : "Fiyatlar Aktif"}
            </h3>
          </div>
          {frozen && (
            <>
              <p style={{ margin: "4px 0", fontSize: 13, color: "#7f1d1d" }}>{status.frozen_message}</p>
              <p style={{ margin: "4px 0", fontSize: 12, color: "#991b1b" }}>Sebep: <strong>{reasonLabel}</strong></p>
            </>
          )}
          <div style={{ marginTop: 8, fontSize: 12, color: "#555" }}>
            Son sarrafiye doğrulaması (çekim): <strong>{fmtAge(status.carpan_age_sec)}</strong> önce
            {status.is_stale_now && <span style={{ color: "#ef4444", fontWeight: 700 }}> • eşik aşıldı</span>}
          </div>
        </div>

        {/* Otomatik mod */}
        <div style={{ ...styles.section, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div>
              <h3 style={{ color: "#09301E", margin: 0, fontSize: 15 }}>Otomatik Dondurma</h3>
              <p style={{ fontSize: 12, color: "#64748b", margin: "4px 0 0", maxWidth: 460 }}>
                Sarrafiye çarpan doğrulaması (Ozan çekimi) bu süreden uzun süredir yapılamıyorsa fiyatlar otomatik donar ve e-posta gider. Doğrulama düzelince otomatik açılır.
              </p>
            </div>
            <button onClick={toggleEnabled} disabled={busy} data-testid="fg-toggle-enabled"
              style={{
                padding: "10px 20px", borderRadius: 8, border: "none", cursor: busy ? "wait" : "pointer",
                fontWeight: 700, fontSize: 13, color: "#fff",
                background: status.enabled ? "#22c55e" : "#94a3b8",
              }}>
              {status.enabled ? "Açık" : "Kapalı"}
            </button>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginTop: 14, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>Donma Eşiği (dakika)</label>
              <input type="number" min="1" max="240" value={threshold} data-testid="fg-threshold-input"
                onChange={(e) => setThreshold(e.target.value)}
                style={{ ...styles.input, marginBottom: 0, fontSize: 14, padding: 10 }} />
            </div>
            <button onClick={saveThreshold} disabled={busy} data-testid="fg-save-threshold"
              style={{ ...styles.button, width: "auto", padding: "13px 22px" }}>
              Kaydet
            </button>
          </div>
          <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>
            Not: Normal çekim aralığı 15 dakikadır. Tek bir çekim kaçırılırsa fiyatlar donar.
          </p>
        </div>

        {/* Manuel kontrol */}
        <div style={styles.section}>
          <h3 style={{ color: "#09301E", margin: "0 0 8px", fontSize: 15 }}>Manuel Kontrol</h3>
          <p style={{ fontSize: 12, color: "#64748b", margin: "0 0 12px" }}>
            Hiçbir sorun olmasa bile fiyatları manuel dondurabilir veya tekrar açabilirsiniz.
          </p>
          {!frozen || status.frozen_reason !== "manual" ? (
            <>
              <input type="text" placeholder="Donma sebebi (opsiyonel, e-postada görünür)" value={manualMsg}
                data-testid="fg-manual-message"
                onChange={(e) => setManualMsg(e.target.value)}
                style={{ ...styles.input, fontSize: 14, padding: 10 }} />
              <button onClick={doFreeze} disabled={busy} data-testid="fg-manual-freeze-btn"
                style={{ width: "100%", padding: 13, background: "#ef4444", color: "#fff", border: "none", borderRadius: 8, cursor: busy ? "wait" : "pointer", fontWeight: 700, fontSize: 14 }}>
                🔒 Fiyatları Manuel Dondur
              </button>
            </>
          ) : (
            <button onClick={doUnfreeze} disabled={busy} data-testid="fg-manual-unfreeze-btn"
              style={{ width: "100%", padding: 13, background: "#22c55e", color: "#fff", border: "none", borderRadius: 8, cursor: busy ? "wait" : "pointer", fontWeight: 700, fontSize: 14 }}>
              🔓 Fiyatları Tekrar Aç
            </button>
          )}
          {frozen && status.frozen_reason === "auto" && (
            <p style={{ fontSize: 12, color: "#92400e", marginTop: 10, background: "#fef3c7", padding: 10, borderRadius: 6 }}>
              Şu an otomatik donma aktif. Doğrulama düzelince fiyatlar kendiliğinden açılacak.
            </p>
          )}
        </div>

        <button onClick={() => window.location.href = profile === "default" ? "/" : `/${profile}`} style={{ ...styles.backButton, marginTop: 15 }}>
          Fiyat Sayfasına Dön
        </button>
      </div>
    </div>
  );
};

export default FiyatGuvenligiTab;
